/**
 * useCommandHistory Hook
 *
 * Implements undo/redo functionality using the command pattern.
 * Maintains a history of commands that can be undone and redone.
 */

import { useState, useCallback, useEffect } from 'react';
import type { Redaction } from '../types';

const MAX_HISTORY_SIZE = 100;

interface Command {
  id: string;
  type: 'add' | 'remove' | 'modify';
  redaction: Redaction;
  previousState?: Redaction;
}

interface CommandHistoryState {
  redactions: Redaction[];
  undoStack: Command[];
  redoStack: Command[];
}

interface CommandHistoryReturn {
  redactions: Redaction[];
  canUndo: boolean;
  canRedo: boolean;
  addRedaction: (redaction: Omit<Redaction, 'id' | 'createdAt'>) => void;
  removeRedaction: (id: string) => void;
  modifyRedaction: (id: string, changes: Partial<Redaction>) => void;
  undo: () => void;
  redo: () => void;
  clearHistory: () => void;
  setRedactions: (redactions: Redaction[]) => void;
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export function useCommandHistory(initialRedactions: Redaction[] = []): CommandHistoryReturn {
  const [state, setState] = useState<CommandHistoryState>({
    redactions: initialRedactions,
    undoStack: [],
    redoStack: [],
  });

  // Execute a command and add it to the undo stack
  const executeCommand = useCallback((command: Command) => {
    setState(prev => {
      let newRedactions: Redaction[];

      switch (command.type) {
        case 'add':
          newRedactions = [...prev.redactions, command.redaction];
          break;
        case 'remove':
          newRedactions = prev.redactions.filter(r => r.id !== command.redaction.id);
          break;
        case 'modify':
          newRedactions = prev.redactions.map(r =>
            r.id === command.redaction.id ? command.redaction : r
          );
          break;
        default:
          newRedactions = prev.redactions;
      }

      // Add to undo stack, clear redo stack
      const newUndoStack = [...prev.undoStack, command].slice(-MAX_HISTORY_SIZE);

      return {
        redactions: newRedactions,
        undoStack: newUndoStack,
        redoStack: [], // Clear redo stack on new action
      };
    });
  }, []);

  // Add a new redaction
  const addRedaction = useCallback((redactionData: Omit<Redaction, 'id' | 'createdAt'>) => {
    const redaction: Redaction = {
      ...redactionData,
      id: generateId(),
      createdAt: new Date(),
    };

    const command: Command = {
      id: generateId(),
      type: 'add',
      redaction,
    };

    executeCommand(command);
  }, [executeCommand]);

  // Remove a redaction
  const removeRedaction = useCallback((id: string) => {
    setState(prev => {
      const redaction = prev.redactions.find(r => r.id === id);
      if (!redaction) return prev;

      const command: Command = {
        id: generateId(),
        type: 'remove',
        redaction,
      };

      const newRedactions = prev.redactions.filter(r => r.id !== id);
      const newUndoStack = [...prev.undoStack, command].slice(-MAX_HISTORY_SIZE);

      return {
        redactions: newRedactions,
        undoStack: newUndoStack,
        redoStack: [],
      };
    });
  }, []);

  // Modify a redaction
  const modifyRedaction = useCallback((id: string, changes: Partial<Redaction>) => {
    setState(prev => {
      const previousRedaction = prev.redactions.find(r => r.id === id);
      if (!previousRedaction) return prev;

      const newRedaction: Redaction = {
        ...previousRedaction,
        ...changes,
        id: previousRedaction.id, // Ensure ID doesn't change
      };

      const command: Command = {
        id: generateId(),
        type: 'modify',
        redaction: newRedaction,
        previousState: previousRedaction,
      };

      const newRedactions = prev.redactions.map(r =>
        r.id === id ? newRedaction : r
      );
      const newUndoStack = [...prev.undoStack, command].slice(-MAX_HISTORY_SIZE);

      return {
        redactions: newRedactions,
        undoStack: newUndoStack,
        redoStack: [],
      };
    });
  }, []);

  // Undo the last command
  const undo = useCallback(() => {
    setState(prev => {
      if (prev.undoStack.length === 0) return prev;

      const command = prev.undoStack[prev.undoStack.length - 1];
      let newRedactions: Redaction[];

      // Reverse the command
      switch (command.type) {
        case 'add':
          // Undo add = remove
          newRedactions = prev.redactions.filter(r => r.id !== command.redaction.id);
          break;
        case 'remove':
          // Undo remove = add back
          newRedactions = [...prev.redactions, command.redaction];
          break;
        case 'modify':
          // Undo modify = restore previous state
          if (command.previousState) {
            newRedactions = prev.redactions.map(r =>
              r.id === command.redaction.id ? command.previousState! : r
            );
          } else {
            newRedactions = prev.redactions;
          }
          break;
        default:
          newRedactions = prev.redactions;
      }

      return {
        redactions: newRedactions,
        undoStack: prev.undoStack.slice(0, -1),
        redoStack: [...prev.redoStack, command],
      };
    });
  }, []);

  // Redo the last undone command
  const redo = useCallback(() => {
    setState(prev => {
      if (prev.redoStack.length === 0) return prev;

      const command = prev.redoStack[prev.redoStack.length - 1];
      let newRedactions: Redaction[];

      // Re-execute the command
      switch (command.type) {
        case 'add':
          newRedactions = [...prev.redactions, command.redaction];
          break;
        case 'remove':
          newRedactions = prev.redactions.filter(r => r.id !== command.redaction.id);
          break;
        case 'modify':
          newRedactions = prev.redactions.map(r =>
            r.id === command.redaction.id ? command.redaction : r
          );
          break;
        default:
          newRedactions = prev.redactions;
      }

      return {
        redactions: newRedactions,
        undoStack: [...prev.undoStack, command],
        redoStack: prev.redoStack.slice(0, -1),
      };
    });
  }, []);

  // Clear all history
  const clearHistory = useCallback(() => {
    setState(prev => ({
      ...prev,
      undoStack: [],
      redoStack: [],
    }));
  }, []);

  // Set redactions directly (e.g., when loading from server)
  const setRedactions = useCallback((redactions: Redaction[]) => {
    setState({
      redactions,
      undoStack: [],
      redoStack: [],
    });
  }, []);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if user is typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
      }

      if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
        e.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);

  return {
    redactions: state.redactions,
    canUndo: state.undoStack.length > 0,
    canRedo: state.redoStack.length > 0,
    addRedaction,
    removeRedaction,
    modifyRedaction,
    undo,
    redo,
    clearHistory,
    setRedactions,
  };
}
